import type { AudioInputDescriptor } from './audioInputs';

export interface EffectClockMirror {
  on?: boolean;
  mode?: 'wall' | 'music' | string;
  bpm?: number | null;
  locked?: boolean;
}

/** The room's durable music-input selection as the controller mirrors it. */
export interface MusicInputMirror {
  selected?: boolean;
  kind?: string;
  id?: string;
  label?: string;
  /**
   * Whether the room is LISTENING to that input — a durable room state the operator switches, separate
   * from having chosen an input and separate again from sound actually arriving.
   *
   * Absent means listening. A controller that has no such switch listens continuously the moment an
   * input is selected, so reading a missing field as "off" would show every such room as deaf.
   */
  listening?: boolean;
  channelGainDb?: number[] | null;
  channelDenoise?: boolean[] | null;
  channelDenoiseMode?: string[] | null;
}

/**
 * Whether the room is listening, from the mirrored selection alone.
 *
 * A room with no input cannot listen, whatever the field says.
 */
export function roomIsListening(input: MusicInputMirror | undefined): boolean {
  return Boolean(input) && input?.listening !== false;
}

/**
 * The `set_music_input` payload for one picker entry. Which sound the room listens to is a room
 * setting the operator commits once on the Music tab — it is never attached to a start or to
 * `set_effect_settings` addresses, which answer "does this effect follow the music".
 */
export type MusicInputCommandInput =
  | { kind: 'device'; identity: string }
  | { kind: 'synthetic'; signal: string }
  | { kind: 'file'; path: string };

export function musicInputCommandInput(
  input: AudioInputDescriptor | undefined,
): MusicInputCommandInput | undefined {
  if (!input || input.available === false) return undefined;
  if (input.kind === 'synthetic') {
    const signal = String(input.signal ?? '');
    return signal ? { kind: 'synthetic', signal } : undefined;
  }
  if (input.kind === 'file') {
    const path = String(input.identity ?? '').replace(/^file:/, '');
    return path ? { kind: 'file', path } : undefined;
  }
  return input.identity ? { kind: 'device', identity: input.identity } : undefined;
}

/**
 * One short line under the switch. It answers the only three questions an operator has: is there
 * anything to follow, is the controller still finding the beat, and what tempo did it settle on.
 */
export function effectSyncIndicator(
  requested: boolean,
  hasInput: boolean,
  status: EffectClockMirror | undefined,
): string {
  if (!requested) return '';
  if (!hasInput || status?.mode === 'wall') return 'No music input';
  const bpm = Number(status?.bpm);
  if (status?.locked && Number.isFinite(bpm) && bpm > 0) return `♪ ${Math.round(bpm)} BPM · locked`;
  return 'Listening…';
}
